import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Button, FormControl, FormHelperText, InputLabel, OutlinedInput, Stack, Typography } from '@mui/material';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { IDistributionList, IPostDistributionListGenericTeam } from '../../../../shared/services/distribution-list/distribution-list.model';
import DistributionListService from '../../../../shared/services/distribution-list/distribution-list.service';
import DistributionListAddressService from '../../../../shared/services/distribution-list-address/distribution-list-address.service';
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

export const DistributionListGenericTeam: FunctionComponent<{
  newDistributionList: IPostDistributionListGenericTeam,
  closeHandler: () => void,
  successHandler: () => void,
  distributionListDetails?: IDistributionList,
}> = ({
  closeHandler,
  successHandler,
  newDistributionList,
  distributionListDetails
}) => {
    const userNotificationsContext = useContext(UserNotificationsContext)
    const distributionListService = new DistributionListService()
    const distributionListAddressService = new DistributionListAddressService()
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const distributionListSchema = Yup.object().shape({
      listName: Yup.string().required('List Name is required'),
      isGenericTeamEmail: Yup.boolean().required(),
      emailAddress: Yup.string().email('Invalid email address').required('You must provide an email address')
    });

    const formik = useFormik({
      initialValues: {
        listName: newDistributionList.listName,
        isGenericTeamEmail: newDistributionList.isGenericTeamEmail,
        emailAddress: newDistributionList.emailAddress,
      },
      validationSchema: distributionListSchema,
      onSubmit: async (values) => {
        if (distributionListDetails) {
          if (!isSubmitting) {
            setIsSubmitting(true)
            distributionListAddressService.editDistributionListAddress(distributionListDetails.distributionListAddresses[0].id, { emailAddress: values.emailAddress, distributionListId: distributionListDetails.id })
              .then(response => {
                formik.resetForm()
                successHandler()
                setIsSubmitting(false)
              })
              .catch(err => {
                setIsSubmitting(false)
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                console.log(err)
              })
          }
        } else {
          if (!isSubmitting) {
            setIsSubmitting(true)
            distributionListService.createDistributionList(values)
              .then(response => {
                successHandler()
                formik.resetForm()
                setIsSubmitting(false)
              })
              .catch(err => {
                setIsSubmitting(false)
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary'  });
                console.log(err)
              })
          }
        }
      },
      validateOnChange: false,
      validateOnBlur: false,
    });

    const cancelHandler = () => {
      closeHandler();
      formik.resetForm()
    };

    useEffect(() => {
      if (distributionListDetails) {
        formik.setFieldValue('listName', distributionListDetails.listName)
        formik.setFieldValue('emailAddress', distributionListDetails.distributionListAddresses[0].emailAddress)
      }
    }, [])

    return (
      <>
        <Box>
          {!distributionListDetails && <FormControl fullWidth variant='outlined' sx={{ mt: 2, mb: 2 }}>
            <InputLabel htmlFor='listName'>Distribution List Name</InputLabel>
            <OutlinedInput
              id='listName'
              name='listName'
              type='text'
              label='Distribution List Name'
              value={formik.values.listName}
              onChange={formik.handleChange}
              error={formik.touched.listName && Boolean(formik.errors.listName)}
            />
            {formik.touched.listName && formik.errors.listName && (
              <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>{formik.errors.listName}</FormHelperText>
            )}
          </FormControl>}
          <Box sx={{ mb: 2, mt: distributionListDetails ? 2 : 0 }}>
            <FormControl fullWidth variant='outlined'>
              <InputLabel htmlFor={`emailAddress`}>Email Address</InputLabel>
              <OutlinedInput
                id={`emailAddress`}
                name={`emailAddress`}
                type='email'
                label='Email Address'
                value={formik.values.emailAddress}
                onChange={formik.handleChange}
                error={formik.touched.emailAddress && Boolean(formik.errors.emailAddress)}
              />
              {formik.errors.emailAddress && formik.touched.emailAddress && (
                <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>
                  {formik.errors.emailAddress}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Stack display='flex' flexDirection='row' justifyContent='flex-end' alignItems='center' gap={1}>
          <DialogSaveButtons
            cancelHandler={cancelHandler}
            submitHandler={formik.submitForm}
            isSubmitting={isSubmitting}
          />
        </Stack>
      </>
    );
  };
